import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { TableComponent } from "@components/table/table.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject, Observable, Subject, from } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { SortimentDTO } from "~app/common/models/common/SortimentDTO";
import { KontraktsradInfoDTO } from "~app/common/models/planera/KontraktsradInfoDTO";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { DestineringsalternativViewModel } from "../arbetsorder-destineringsalternativ/arbetsorder-destineringsalternativ.models";
import { SetExtraSortimentCommand } from "../commands/SetExtraSortimentCommand";
import { SetKontraktsradJusteringCommand } from "../commands/SetKontraktsradJusteringCommand";
import { ArbetsorderWorkEventMessage } from "../events/ArbetsorderWorkEventMessage";
import { ArbetsorderService } from "../services/arbetsorder.service";
import { ArbetsorderKontraktsraderValidation } from "./arbetsorder-kontraktsrader.validation";

@Component({
    selector: "rask-arbetsorder-kontraksrader",
    templateUrl: "./arbetsorder-kontraksrader.component.html",
    styleUrls: ["./arbetsorder-kontraksrader.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MessagePanelComponent,
        InputComponent,
        TableComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent,
        BusySpinnerComponent,
        ButtonComponent,
        AsyncPipe
    ]
})
export class ArbetsorderKontraksraderComponent implements OnInit, OnDestroy {
    activeModal = inject(NgbActiveModal);
    allaKontraktsrader: KontraktsradInfoDTO[];
    data: {
        arbetsorderId: any;
        alternativ: DestineringsalternativViewModel;
        canEdit: boolean;
        extraSortiment: SortimentDTO[];
    };
    destineringsursprung: string;
    form = new UntypedFormGroup({
        extraSortiment: new UntypedFormControl([])
    });
    formDirty: boolean;
    isLoading = false;
    messages$: BehaviorSubject<IMessageModel[]> = new BehaviorSubject<IMessageModel[]>([]);
    messageItemsAsObservable: Observable<IMessageModel[]> = this.messages$.asObservable();
    tableColumns: RaskColumn[];
    validationContext = ArbetsorderKontraktsraderValidation.init();

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly eventService = inject(EventService);
    private readonly klar = "Klar";
    private readonly minSortimentLength = 4;
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly status = "Completed";
    private readonly vilande = "Vilande";
    private readonly work = "viol2.spegla.started";

    constructor() {
        this.commandManager = new CommandManager();
    }

    canSave() {
        if (this.formDirty && !this.validationContext.hasErrors(ValidationScopes.spara) && !this.isAllaRaderVilande()) {
            return true;
        }
        return undefined;
    }

    close(): void {
        this.activeModal.close();
    }

    extraSortimentChanged() {
        if (!ObjectUtils.hasValue(this.data.extraSortiment)) {
            return;
        }

        const formIds = this.form.value.extraSortiment.map(x => x.id);
        const orgIds = this.data.extraSortiment.map(x => x.id);
        // eslint-disable-next-line sonarjs/no-misleading-array-reverse, sonarjs/no-alphabetical-sort
        const isEqual = (JSON.stringify(formIds.sort()) === JSON.stringify(orgIds.sort()));

        if (!isEqual) {
            this.formDirty = true;
        }
    }

    initData() {
        this.arbetsorderService.getExtraSortiment(this.data.alternativ.id).subscribe(res => {
            this.data.extraSortiment = res;
            res.forEach(x => x.namn = `${x.kod} - ${x.namn}`);
            this.form.get("extraSortiment").setValue(res);
        });
        this.arbetsorderService.refreshKontraktsrader(this.data.arbetsorderId, this.data.alternativ.id);
        this.arbetsorderService.allaKontraktsrader$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                this.allaKontraktsrader = x;
                this.onNewKontraktsrder(x);
            });
        this.initColumns();
        this.destineringsursprung = this.getDestineringsUrsprung();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initData();

        this.eventService.subscribeEvent(ArbetsorderWorkEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: any) => {
                if (event.work === this.work && event.status === this.status && this.isLoading) {
                    this.isLoading = false;
                    this.initData();
                }
            });
    }

    onNewKontraktsrder(rader: KontraktsradInfoDTO[]) {
        const mplTitle = `${this.data.alternativ.mottagningsplatsNamn}  ${this.data.alternativ.mottagningsplatsnummer}`;
        const messages = rader
            .filter(p => !this.isRadReadOnly(p)) // Rendera inte meddelande på ej editerbara rader
            .map(rad => rad.destineringar
                .filter(p => p.destineringUid !== this.data.alternativ.destineringUid)
                .map(dest => {
                    const hsTitle = dest.handelssortimentNamn + (dest.foradlatHandelssortimentNamn != null ? `/${dest.foradlatHandelssortimentNamn}` : "");
                    return {
                        severity: MessageSeverity.Info,
                        message: `De ändringar som görs här för SSTE ${rad.sste} kommer även att slå igenom på virkesorderrader för ${hsTitle} till ${mplTitle}`
                    } as IMessageModel;
                }))
            .reduce((prev, curr) => prev.concat(curr), []);

        this.messages$.next(messages);
    }

    searchForSortiment = (searchString: string) => this.arbetsorderService.searchSortimentTypeAhead(searchString, this.getSsteToExclude(), this.minSortimentLength);

    spara() {
        this.allaKontraktsrader.forEach(x => {
            this.commandManager.addOrUpdate(new SetKontraktsradJusteringCommand(c => {
                c.affarsledJusterat = x.affarsledJusterat;
                c.kollektivJusterat = x.kollektivJusterat;
                c.matmetodAJusterat = x.matmetodAJusterat;
                c.matmetodBJusterat = x.matmetodBJusterat;
                c.matmetodCJusterat = x.matmetodCJusterat;
                c.matmetodDJusterat = x.matmetodDJusterat;
                c.transportalternativ9Justerat = x.transportalternativ9Justerat;
                c.arbetsorderContractLineId = x.arbetsorderContractLineId;
                c.voStatusRadJusterat = x.statusJusterat;
            }), c => c.arbetsorderContractLineId === x.arbetsorderContractLineId);
        });

        this.commandManager.addOrUpdate(new SetExtraSortimentCommand(c => {
            c.destineringsId = this.data.alternativ.id;
            c.sortimentIds = this.form.value.extraSortiment.map(x => x.id);
        }), null);

        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService
            .addWork(work)
            .subscribe(() => {
                this.formDirty = false;
                this.commandManager.clear();
                this.isLoading = true;
            });
    }

    private getDestineringsUrsprung(): string {
        const res = [];

        if (this.data.alternativ.isKundorderrad) {
            res.push("Kundorderrad");
        }
        if (this.data.alternativ.isAlternativt) {
            res.push("Alternativt handelssortiment");
        }
        if (this.data.alternativ.isRelaterat) {
            res.push("Relaterat handelssortiment");
        }
        if (this.data.alternativ.isExtraMottagningsplats) {
            res.push("Extra mottagningsplats");
        }
        if (this.data.alternativ.isAlternativMottagningsplats) {
            res.push("Alternativ mottagningsplats");
        }
        if (this.data.alternativ.isForadlatHandelssortiment) {
            res.push("Förädlat handelssortiment");
        }
        if (this.data.alternativ.isOgiltig) {
            res.push("Okänd");
        }
        return res.join(", ");
    }

    private getSsteToExclude(): string[] {
        const extraSortiment = this.data.extraSortiment.map(x => x.kod);
        const sortiment = this.allaKontraktsrader.map(x => x.sste);
        return sortiment.concat(extraSortiment);
    }

    private initColumns() {
        this.tableColumns = [
            {
                readonly: this.isRadReadOnly,
                headerName: "SSTE",
                field: "sste",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.affarsled),
                headerName: "AL-kod",
                rule: this.validationContext.current.AlKod,
                field: "affarsledJusterat",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                minWidth: 140,
                type: "input",
                dataChanged: new Subject()
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.matmetodA),
                headerName: "A",
                rule: this.validationContext.current.Matmetod,
                field: "matmetodAJusterat",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                minWidth: 60,
                type: "input",
                dataChanged: new Subject()
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.matmetodB),
                headerName: "B",
                rule: this.validationContext.current.Matmetod,
                field: "matmetodBJusterat",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                minWidth: 60,
                type: "input",
                dataChanged: new Subject()
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.kollektiv),
                headerName: "Kollektiv",
                rule: this.validationContext.current.Kollektiv,
                field: "kollektivJusterat",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                minWidth: 100,
                type: "input",
                dataChanged: new Subject()
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.matmetodC),
                headerName: "C",
                rule: this.validationContext.current.Matmetod,
                field: "matmetodCJusterat",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                minWidth: 60,
                type: "input",
                dataChanged: new Subject()
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.matmetodD),
                headerName: "D",
                rule: this.validationContext.current.Matmetod,
                field: "matmetodDJusterat",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                minWidth: 60,
                type: "input",
                dataChanged: new Subject()
            },
            {
                readonly: (input) => true,
                headerName: "Prislista",
                field: "prislista",
                filter: false,
                sortable: false,
                minWidth: 100,
                type: "span"
            },
            {
                readonly: (input) => true,
                headerName: "Avlpts",
                field: "avlastningsplats",
                filter: false,
                sortable: false,
                minWidth: 50,
                type: "span"
            },
            {
                readonly: this.isRadReadOnly,
                headerName: "Trp.alt.9",
                field: "transportalternativ9Justerat",
                filter: false,
                sortable: false,
                minWidth: 50,
                type: "checkbox",
                dataChanged: new Subject()
            },
            {
                readonly: this.isRadReadOnly,
                placeholder: (input) => (input.status),
                editable: this.data.canEdit,
                viewFormatter: (input) => input ? input : undefined,
                formatter: (input) => input ? input : undefined,
                headerName: "Status",
                field: "statusJusterat",
                filter: false,
                sortable: false,
                minWidth: 211,
                type: "selector",
                values: [
                    this.klar,
                    this.vilande
                ],
                dataChanged: new Subject()
            }
        ] as any[] as RaskColumn[];
        this.setDataChanged();
    }

    private isAllaRaderVilande(): boolean {
        if (this.allaKontraktsrader.length === 0) {
            return false;
        }

        const statusesJusterat = this.allaKontraktsrader.filter(x => x.statusJusterat === this.klar).map(x => x.statusJusterat);
        const statuses = this.allaKontraktsrader.filter(x => x.statusJusterat === null && x.status === this.klar).map(x => x.status);
        const allStatues = statuses.concat(statusesJusterat);

        if (allStatues.includes(this.klar)) {
            this.messages$.next([]);
            return false;
        }

        const messages =  [
{
    severity: MessageSeverity.Info,
    message: "Alla rader får inte vara vilande"
} as IMessageModel
        ];

        this.messages$.next(messages);
        return true;
    }

    private readonly isRadReadOnly = (input: KontraktsradInfoDTO) => {
        if (input.status === this.vilande && input.statusJusterat === null) {
            return true;
        }
        if (!input.sste || !input.mottagningsplatsnummer || !input.mottagareHuvudkod) {
            return true;
        }

        if(!this.data?.canEdit) {
            return true;
        }
        return false;
    };

    private setDataChanged() {
        this.tableColumns.forEach(x => {
            if (x.dataChanged) {
                x.dataChanged
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(this.setFormDirty);
            }
        });
    }

    private readonly setFormDirty = () => {
        this.formDirty = true;
    };
}
